import isNil from 'lodash/isNil'
import orderBy from 'lodash/orderBy'
import React from 'react'
import ErrorLabel from 'ui/atoms/ErrorLabel'
import Checkbox from 'ui/Checkbox'
import { HelperText, QuestionTitle } from 'ui/Text'

import { TriggerUserActions } from '@/components/TriggerUserActions'
import { useFormAppearance } from '@/hooks/useFormAppearance'
import cn from '@/utils/tailwind/cn'

import { useMatrixColumnSpans } from '../../../hooks/useMatrixColumnSpans'
import { hasText } from '../../../utils/helpers'

const HighlightHeaderClasses =
  'px-3 py-3 border-b border-gray-200 bg-gray-50 text-left text-sm leading-4 font-medium text-txt-emphasis tracking-wider'

export function MatrixCheckboxRender({
  errorMessage,
  getCellValue = () => false,
  handleCellChange = () => {},
  hasError,
  help_text,
  isDirty,
  isRequired,
  isValid,
  isOfficeUse,
  label,
  question_columns,
  question_rows,
  readonly,
  willShowNotAllowedCursor,
  questionId,
  response,
  sessions,
}) {
  const { hasQuestionColor } = useFormAppearance()
  function isRowError(rowId) {
    return question_columns.every((c) => !getCellValue(rowId, c.id))
  }

  const [firstColSpan, totalColSpan] = useMatrixColumnSpans(
    question_columns?.length ?? 0,
    'checkbox'
  )

  return (
    <div>
      {label && (
        <QuestionTitle isOfficeUse={isOfficeUse} isRequired={isRequired}>
          {label}
        </QuestionTitle>
      )}
      {hasText(help_text) && (
        <HelperText className="mb-2" marginTop={0}>
          {help_text}
        </HelperText>
      )}

      {/* small devices */}
      <div className="overflow-hidden rounded border border-gray-200 md:hidden">
        {orderBy(question_rows, ['order'], ['asc']).map((r) => (
          <div key={r.id}>
            <div
              className={cn(HighlightHeaderClasses, {
                'text-red-500':
                  isDirty && isRequired && !isValid && isRowError(r.id),
                'text-txt':
                  !isDirty || !isRequired || isValid || !isRowError(r.id),
              })}
            >
              <div
                className={cn(
                  hasQuestionColor && 'question-text',
                  'flex flex-col text-sm text-txt-emphasis'
                )}
              >
                <span>{r.text}</span>
                {hasText(r?.help_text) && (
                  <span className="text-xs font-normal text-txt">
                    {r.help_text}
                  </span>
                )}
              </div>
            </div>
            {orderBy(question_columns, ['order'], ['asc']).map((c) => (
              <div
                className={cn(
                  hasQuestionColor && 'question-text',
                  'border-b border-gray-200 px-6 pt-2 text-xs leading-4 tracking-wider hover:bg-gray-100',
                  {
                    'cursor-not-allowed': willShowNotAllowedCursor,
                    'cursor-pointer': !willShowNotAllowedCursor,
                  }
                )}
                key={c.id}
                onClick={() => handleCellChange(r, c)}
              >
                <TriggerUserActions>
                  <Checkbox
                    checked={getCellValue(r.id, c.id)}
                    data-form-id={response?.form?.id}
                    data-packet-id={
                      sessions?.fillResponseCustomer?.packetResponse?.packet?.id
                    }
                    data-response-id={response?.id}
                    disabled={readonly}
                    hasError={
                      isDirty && isRequired && !isValid && isRowError(r.id)
                    }
                    id={`q-${questionId}-r-${r.id}-c-${c.id}`}
                    willShowNotAllowedCursor={willShowNotAllowedCursor}
                  >
                    <span>{c.header}</span>
                    {!isNil(c.number_value) && (
                      <span className="text-xs text-gray-400">
                        {` (${c.number_value})`}
                      </span>
                    )}
                  </Checkbox>
                </TriggerUserActions>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* medium and above */}
      <div className="hidden overflow-hidden rounded border border-gray-200 md:block">
        {/* HEADER */}
        <div
          className="grid"
          style={{
            gridTemplateColumns: `repeat(${totalColSpan}, minmax(0, 1fr))`,
          }}
        >
          <div
            className={`${HighlightHeaderClasses} flex items-center`}
            style={{
              gridColumn: `span ${firstColSpan} / span ${firstColSpan}`,
            }}
          />
          {orderBy(question_columns, ['order'], ['asc']).map((c) => (
            <div
              className={cn(
                hasQuestionColor && 'question-text',
                HighlightHeaderClasses,
                'flex flex-col items-center justify-center text-center'
              )}
              key={c.id}
            >
              <span>{c.header}</span>
              {!isNil(c.number_value) && (
                <span className="mt-1 text-xs text-gray-400">
                  {c.number_value}
                </span>
              )}
            </div>
          ))}
        </div>
        {/* BODY */}
        <div className="bg-white">
          {orderBy(question_rows, ['order'], ['asc']).map((r, i) => (
            <div
              className="grid"
              key={r.id}
              style={{
                gridTemplateColumns: `repeat(${totalColSpan}, minmax(0, 1fr))`,
              }}
            >
              <div
                className={`${HighlightHeaderClasses} ${
                  i % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                } flex items-center`}
                style={{
                  gridColumn: `span ${firstColSpan} / span ${firstColSpan}`,
                }}
              >
                <div
                  className={cn(
                    hasQuestionColor && 'question-text',
                    'flex flex-col space-y-1'
                  )}
                >
                  <span>{r.text}</span>
                  {hasText(r?.help_text) && (
                    <span className="text-xs font-normal text-txt">
                      {r.help_text}
                    </span>
                  )}
                </div>
              </div>

              {orderBy(question_columns, ['order'], ['asc']).map((c) => (
                <div
                  className={cn(
                    hasQuestionColor && 'question-text',
                    HighlightHeaderClasses,
                    {
                      'bg-white': i % 2 === 0,
                      'bg-gray-50': i % 2 !== 0,
                    }
                  )}
                  key={c.id}
                >
                  <div
                    className={`flex items-center justify-center rounded pt-2 hover:bg-gray-100${
                      willShowNotAllowedCursor
                        ? 'cursor-not-allowed'
                        : 'cursor-pointer'
                    } flex items-center`}
                    key={c.id}
                    onClick={() => handleCellChange(r, c)}
                  >
                    <TriggerUserActions>
                      <Checkbox
                        useDisplayBlock
                        checked={getCellValue(r.id, c.id)}
                        data-form-id={response?.form?.id}
                        data-packet-id={
                          sessions?.fillResponseCustomer?.packetResponse?.packet
                            ?.id
                        }
                        data-response-id={response?.id}
                        disabled={readonly}
                        hasError={
                          isDirty && isRequired && !isValid && isRowError(r.id)
                        }
                        willShowNotAllowedCursor={willShowNotAllowedCursor}
                      />
                    </TriggerUserActions>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      {hasError && (
        <div>
          <ErrorLabel>{errorMessage}</ErrorLabel>
        </div>
      )}
    </div>
  )
}

export default MatrixCheckboxRender
